import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";
import { Avatar, AvatarFallback, AvatarImage } from "../../ui/avatar";
import { defaultPfpUrl } from "../../utils";
import { cn } from "../../../lib/utils";

/**
 * Modal listing who reacted, grouped by emoji.
 * @param {Object} props
 * @param {boolean} props.open
 * @param {(open: boolean) => void} props.onOpenChange
 * @param {Array<Object>} props.reactions - Aggregated reaction groups
 * @param {number} props.userId - Current user id
 * @param {(reactionId: number) => void} [props.onRemoveOwn] - Remove own reaction
 * @returns {JSX.Element}
 */
function ReactionsModal({
  open,
  onOpenChange,
  reactions,
  userId,
  onRemoveOwn,
}) {
  const groups = useMemo(
    () => (Array.isArray(reactions) ? reactions : []),
    [reactions]
  );
  const [activeEmoji, setActiveEmoji] = useState("all");

  const tabs = useMemo(() => {
    const total = groups.reduce((sum, g) => sum + (g.count || 0), 0);
    return [
      { key: "all", label: "All", count: total },
      ...groups.map((g) => ({
        key: g.emoji,
        label: g.emoji,
        count: g.count || 0,
      })),
    ];
  }, [groups]);

  const visibleUsers = useMemo(() => {
    if (activeEmoji === "all") {
      return groups.flatMap((g) =>
        (g.users || []).map((u) => ({ ...u, emoji: g.emoji }))
      );
    }
    const group = groups.find((g) => g.emoji === activeEmoji);
    return (group?.users || []).map((u) => ({
      ...u,
      emoji: group.emoji,
    }));
  }, [activeEmoji, groups]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm p-0">
        <DialogHeader>
          <DialogTitle>Reactions</DialogTitle>
        </DialogHeader>
        <div className="flex gap-1 overflow-x-auto border-b border-ui-border px-3 py-2">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={cn(
                "shrink-0 rounded-md px-2.5 py-1 text-sm transition-colors",
                activeEmoji === tab.key
                  ? "bg-highlight text-text-primary"
                  : "text-text-muted hover:bg-ui-muted hover:text-text-primary"
              )}
              onClick={() => setActiveEmoji(tab.key)}
            >
              <span className="mr-1">{tab.label}</span>
              <span className="text-xs text-text-muted">{tab.count}</span>
            </button>
          ))}
        </div>
        <div className="max-h-72 overflow-y-auto px-2 py-2">
          {visibleUsers.length === 0 ? (
            <p className="px-3 py-6 text-center text-sm text-text-muted">
              No reactions yet
            </p>
          ) : (
            <ul className="space-y-1">
              {visibleUsers.map((user) => {
                const isMe = Number(user.user_id) === Number(userId);
                return (
                  <li
                    key={`${user.reaction_id}-${user.user_id}`}
                    className="flex items-center gap-3 rounded-md px-2 py-2 hover:bg-ui-muted"
                  >
                    <Link
                      to={`/profile/${user.user_id}`}
                      className="flex min-w-0 flex-1 items-center gap-3"
                      onClick={() => onOpenChange(false)}
                    >
                      <Avatar className="h-8 w-8 shrink-0">
                        <AvatarImage
                          src={user.user_pfp || defaultPfpUrl}
                          alt={user.user_name || "User"}
                          onError={(e) => {
                            e.target.src = defaultPfpUrl;
                          }}
                        />
                        <AvatarFallback>
                          {(user.user_name || "?").slice(0, 1).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <span className="truncate text-sm text-text-primary">
                        {isMe ? "You" : user.user_name || "User"}
                      </span>
                    </Link>
                    <span className="text-base" aria-hidden>
                      {user.emoji}
                    </span>
                    {isMe && typeof onRemoveOwn === "function" ? (
                      <button
                        type="button"
                        className="text-xs text-text-muted hover:text-delete"
                        onClick={() => onRemoveOwn(user.reaction_id)}
                      >
                        Remove
                      </button>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default ReactionsModal;
